/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities --
 * v77.99.104: Payment Credits UI — record/edit/allocate/deallocate
 * unallocated lump-sum payments received from a co-parent.
 *
 * Three exports:
 *   • PERIOD_PRESETS, ymd, computeRange — helpers reused by the
 *     Expenses page header for the new date-filter chip bar.
 *   • PeriodChipBar — the chip-style date filter (This month / YTD /
 *     This year / Last 12m / Custom).
 *   • PaymentCreditsPanel — the dedicated tab content. Includes the
 *     list, record-payment dialog, allocate dialog, deallocate
 *     confirm.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Loader2, Wallet, Plus, Check, X, Trash2, AlertCircle, ListChecks, FileWarning,
  Lock, Unlock, MessageSquarePlus, Download, ArrowDownRight, ChevronDown, ChevronRight,
  ShieldCheck,
} from "lucide-react";
import { useChildScope } from "@/context/ChildScopeContext";

export const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const PERIOD_PRESETS = [
  {
    id: "this_month", label: "This month", compute: () => {
      const n = new Date();
      return { from: ymd(new Date(n.getFullYear(), n.getMonth(), 1)),
               to: ymd(new Date(n.getFullYear(), n.getMonth() + 1, 0)) };
    },
  },
  {
    id: "ytd", label: "Year to date", compute: () => {
      const n = new Date();
      return { from: `${n.getFullYear()}-01-01`, to: ymd(n) };
    },
  },
  {
    id: "this_year", label: "This year", compute: () => {
      const n = new Date();
      return { from: `${n.getFullYear()}-01-01`, to: `${n.getFullYear()}-12-31` };
    },
  },
  {
    id: "last_12m", label: "Last 12 months", compute: () => {
      const n = new Date();
      const start = new Date(n.getFullYear(), n.getMonth() - 11, 1);
      return { from: ymd(start), to: ymd(n) };
    },
  },
  {
    // Iter 123.hotfix — "All time" is the safety-net preset. First-time
    // visitors default to last_12m; if their records fall outside that
    // window they can pick this to guarantee nothing is hidden.
    id: "all", label: "All time", compute: () => ({ from: "", to: "" }),
  },
  { id: "custom", label: "Custom" },
];

export function computeRange(presetId, customRange) {
  if (presetId === "custom") return customRange || { from: "", to: "" };
  const preset = PERIOD_PRESETS.find((p) => p.id === presetId);
  return preset && preset.compute ? preset.compute() : { from: "", to: "" };
}

export function PeriodChipBar({ value, onChange, customRange, onCustomChange, currency }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="expenses-period-bar">
      {PERIOD_PRESETS.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onChange(p.id)}
          className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
            value === p.id
              ? "bg-[#116B6E] text-white border-[#116B6E]"
              : "bg-white text-[#2E3A3F] border-[#D9E1E3] hover:bg-[#EEF3F4]"
          }`}
          data-testid={`expenses-period-${p.id}`}
        >
          {p.label}
        </button>
      ))}
      {value === "custom" && (
        <div className="flex items-center gap-2" data-testid="expenses-period-custom">
          <Input type="date" value={customRange?.from || ""}
            onChange={(e) => onCustomChange({ ...customRange, from: e.target.value })}
            className="w-[140px] h-8 text-xs border-[#D9E1E3] rounded-sm"
            data-testid="expenses-period-custom-from" />
          <span className="text-xs text-[#6F828A]">to</span>
          <Input type="date" value={customRange?.to || ""}
            onChange={(e) => onCustomChange({ ...customRange, to: e.target.value })}
            className="w-[140px] h-8 text-xs border-[#D9E1E3] rounded-sm"
            data-testid="expenses-period-custom-to" />
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Payment Credits panel
// -------------------------------------------------------------------

const fmt = (amt, ccy = "EUR") => {
  const n = typeof amt === "number" ? amt : parseFloat(amt || 0);
  return `${ccy} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export function PaymentCreditsPanel({ currency = "EUR", childList = [], coparents = [], onCreditsChanged, pendingAllocationTarget, onPendingHandled }) {
  const [credits, setCredits] = useState([]);
  // v77.99.115f — Direct per-expense payments received by the current
  // user. Combined with `credits` they form the "unified payments
  // received" timeline. Sourced from `/expenses/payments/received-timeline`.
  const [directPayments, setDirectPayments] = useState([]);
  const [timelineTotals, setTimelineTotals] = useState({
    credits_received: 0, direct_received_live: 0, total_received_live: 0,
  });
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recordOpen, setRecordOpen] = useState(false);
  const [allocateFor, setAllocateFor] = useState(null);
  // v77.99.106 — When the parent passes a pendingAllocationTarget (the
  // user clicked "Apply credit" from an expense row), auto-open the
  // AllocateDialog for the credit with the largest remaining balance
  // and pre-fill the target expense + suggested amount.
  const [prefilledPick, setPrefilledPick] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // v77.99.115f — Fetch BOTH the credits list and the combined
      // timeline. We still keep the dedicated `/credits` call for the
      // existing allocate-dialog flow (which needs the full credit
      // shape including `_to_public` fields), and additionally use
      // the timeline endpoint for the direct-payments side + totals.
      const [credResp, timelineResp] = await Promise.all([
        api.get("/expenses/payments/credits"),
        api.get("/expenses/payments/received-timeline"),
      ]);
      setCredits(credResp.data || []);
      const items = timelineResp.data?.items || [];
      setDirectPayments(items.filter((i) => i.kind === "direct"));
      setTimelineTotals(timelineResp.data?.totals || {
        credits_received: 0, direct_received_live: 0, total_received_live: 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load payment credits.");
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadTimelinePdf = useCallback(async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const r = await api.get("/expenses/payments/received-timeline/pdf", {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "payments-received.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded payments-received.pdf");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadingPdf]);

  // v77.99.140 (iter 124.reconciliation-snapshot) — One-page
  // proof-of-books PDF for mediation / solicitor requests. Uses the
  // same reconciled figures the on-screen tiles display, so the
  // recipient can see:  Total received = Allocated (active) +
  // Unallocated  —  with a "Books balanced" verdict at the bottom.
  const [downloadingReconciliation, setDownloadingReconciliation] = useState(false);
  const downloadReconciliationPdf = useCallback(async () => {
    if (downloadingReconciliation) return;
    setDownloadingReconciliation(true);
    try {
      const r = await api.get("/expenses/reconciliation/pdf", {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `reconciliation-snapshot-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Reconciliation snapshot downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate reconciliation PDF.");
    } finally {
      setDownloadingReconciliation(false);
    }
  }, [downloadingReconciliation]);

  useEffect(() => { reload(); }, [reload]);

  // v77.99.106 — Auto-open the AllocateDialog when the parent hands
  // us a `pendingAllocationTarget`. Picks the credit with the largest
  // remaining balance.
  useEffect(() => {
    if (!pendingAllocationTarget || loading || credits.length === 0) return;
    const candidate = credits
      .filter((c) => (c.remaining_amount || 0) > 0.005)
      .sort((a, b) => (b.remaining_amount || 0) - (a.remaining_amount || 0))[0];
    if (!candidate) {
      toast.info("No unallocated credit available right now.");
      onPendingHandled?.();
      return;
    }
    setPrefilledPick({
      expense_id: pendingAllocationTarget.expenseId,
      amount: Math.min(
        candidate.remaining_amount || 0,
        pendingAllocationTarget.suggestedAmount || 0,
      ),
    });
    setAllocateFor(candidate);
    onPendingHandled?.();
  }, [pendingAllocationTarget, credits, loading, onPendingHandled]);

  const totals = useMemo(() => {
    let received = 0, remaining = 0, allocated = 0;
    for (const c of credits) {
      received += c.amount || 0;
      remaining += c.remaining_amount || 0;
      allocated += c.allocated_amount || 0;
    }
    return { received, remaining, allocated };
  }, [credits]);

  const handleDelete = async (credit) => {
    if (!window.confirm(`Delete this payment credit of ${fmt(credit.amount, credit.currency)}? Any allocations will be removed.`)) return;
    try {
      await api.delete(`/expenses/payments/credits/${credit.id}`);
      toast.success("Payment credit deleted.");
      reload();
      onCreditsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="space-y-5" data-testid="payment-credits-panel">
      <Card className="border-[#D9E1E3] rounded-md shadow-none">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#116B6E]" strokeWidth={1.5} />
                <span className="font-heading text-lg text-[#2E3A3F]">Payments received</span>
              </div>
              <p className="text-sm text-[#6F828A] mt-1 max-w-2xl">
                Record lump-sum payments received from a co-parent. These reduce your net outstanding balance without marking individual expenses settled — you can allocate them to specific expenses later, or leave them as a running credit.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
              {/* v77.99.140 (iter 124.reconciliation-snapshot) — One-
                  page proof-of-books PDF for mediation / solicitor
                  requests. Draws from the same reconciled figures on
                  the summary tiles so the recipient can independently
                  verify the ledger identity. */}
              <Button
                variant="outline"
                onClick={downloadReconciliationPdf}
                disabled={downloadingReconciliation}
                className="border-[#A6D7D9] text-[#116B6E] hover:bg-[#D6F0EF] whitespace-nowrap"
                data-testid="payments-reconciliation-download-pdf"
                title="Download a one-page reconciliation snapshot showing how received money reconciles across allocated, released and unallocated pools."
              >
                {downloadingReconciliation
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.6} />
                  : <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={1.6} />}
                Reconciliation snapshot
              </Button>
              {/* v77.99.115f — Download the combined timeline (lump-sum
                  credits + direct per-expense receipts) as a PDF in
                  the Court Bundle style. Useful for sharing with
                  mediators / solicitors who want a single dated audit
                  trail of every payment received. */}
              <Button
                variant="outline"
                onClick={downloadTimelinePdf}
                disabled={downloadingPdf}
                className="border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F8FAFB] whitespace-nowrap"
                data-testid="payments-received-download-pdf"
                title="Download a PDF of every payment received (lump-sum credits + direct payments)."
              >
                {downloadingPdf
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.6} />
                  : <Download className="w-4 h-4 mr-2" strokeWidth={1.6} />}
                Download PDF
              </Button>
              <Button
                onClick={() => setRecordOpen(true)}
                className="bg-[#116B6E] hover:bg-[#0F5658] text-white whitespace-nowrap"
                data-testid="record-payment-button"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.6} />
                Record payment received
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
            <SummaryTile label="Total received" value={fmt(totals.received, currency)} testid="credits-received" />
            <SummaryTile label="Allocated to expenses" value={fmt(totals.allocated, currency)} testid="credits-allocated" />
            <SummaryTile label="Remaining unallocated" value={fmt(totals.remaining, currency)}
              highlight testid="credits-remaining" />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-sm text-[#6F828A] py-6 text-center">
          <Loader2 className="w-4 h-4 inline animate-spin mr-2" strokeWidth={1.8} /> Loading…
        </div>
      ) : credits.length === 0 ? (
        <Card className="border-[#D9E1E3] rounded-md shadow-none">
          <CardContent className="p-8 text-center text-sm text-[#6F828A]">
            No payment credits recorded yet. Use "Record payment received" above when a co-parent sends a lump sum.
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-3" data-testid="credits-list">
          {credits.map((c) => (
            <li key={c.id}>
              <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid={`credit-row-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        <span className="font-mono-data text-base font-semibold text-[#2E3A3F]">
                          {fmt(c.amount, c.currency)}
                        </span>
                        <span className="text-xs text-[#6F828A]">received {c.payment_date}</span>
                        {!c.shared_with_coparent && (
                          <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-[#5C4516] bg-[#FFF8E8] border border-[#E6CFA2] rounded-sm px-1.5 py-0.5">
                            Private
                          </span>
                        )}
                      </div>
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-[#6F828A]">
                        <span>Allocated: {fmt(c.allocated_amount, c.currency)}</span>
                        <span>Remaining: <strong className={c.remaining_amount > 0.005 ? "text-[#116B6E]" : "text-[#6F828A]"}>{fmt(c.remaining_amount, c.currency)}</strong></span>
                        {(c.allocations || []).length > 0 && (
                          <span>· {c.allocations.length} allocation{c.allocations.length === 1 ? "" : "s"}</span>
                        )}
                      </div>
                      {c.note && <p className="text-xs text-[#2E3A3F] mt-2 whitespace-pre-wrap" data-testid={`credit-note-${c.id}`}>{c.note}</p>}
                      {/* v77.99.115d — Append-only notes timeline. Original
                          `note` stays immutable for audit; users add
                          clarifying context here. Stamped with author + ts. */}
                      <CreditNotesTimeline
                        credit={c}
                        onChanged={() => { reload(); onCreditsChanged?.(); }}
                      />
                    </div>
                    <div className="flex gap-2 sm:flex-shrink-0">
                      {/* v77.99.115d — Toggle the credit's privacy in-
                          place. The backend PATCH already supports
                          `shared_with_coparent`, this is just the UI. */}
                      <CreditShareToggle
                        credit={c}
                        onChanged={() => { reload(); onCreditsChanged?.(); }}
                      />
                      {c.remaining_amount > 0.005 && (
                        <Button size="sm" variant="outline"
                          onClick={() => setAllocateFor(c)}
                          className="border-[#A6D7D9] text-[#116B6E] hover:bg-[#D6F0EF]"
                          data-testid={`credit-allocate-${c.id}`}>
                          <ListChecks className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.6} /> Allocate
                        </Button>
                      )}
                      <Button size="sm" variant="ghost"
                        onClick={() => handleDelete(c)}
                        className="text-[#A93A2F] hover:bg-[#FBEFEE]"
                        data-testid={`credit-delete-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {recordOpen && (
        <RecordPaymentDialog
          open={recordOpen}
          onClose={() => setRecordOpen(false)}
          onSaved={() => { setRecordOpen(false); reload(); onCreditsChanged?.(); }}
          currency={currency}
          childList={childList}
          coparents={coparents}
        />
      )}
      {allocateFor && (
        <AllocateDialog
          credit={allocateFor}
          onClose={() => { setAllocateFor(null); setPrefilledPick(null); }}
          onSaved={() => { setAllocateFor(null); setPrefilledPick(null); reload(); onCreditsChanged?.(); }}
          onChanged={() => { reload(); onCreditsChanged?.(); }}
          prefilledPick={prefilledPick}
        />
      )}
      {/* v77.99.115f — Direct per-expense payments received. Sits below
          the lump-sum credits list so the unified "Payments received"
          panel is a single chronological feed when both kinds exist.
          On the user's current household this is empty, but as soon
          as direct payments are recorded against individual expenses
          they appear here too. */}
      <DirectPaymentsReceivedSection
        payments={directPayments}
        currency={currency}
        totals={timelineTotals}
      />
    </div>
  );
}

function DirectPaymentsReceivedSection({ payments, currency, totals }) {
  // v77.99.140 (iter 124.direct-payments-voided-collapse) — Rework of
  // this section per user feedback ("The voided expenses should be
  // grouped and collapsed entirely. Or not shown as they are void
  // and irrelevant").
  //
  //   • Live rows render prominently (default expanded).
  //   • Voided rows are hidden behind a single collapsed
  //     "Show N voided entries" chip. Clicking reveals the group;
  //     re-clicking hides it again. Voided rows keep their
  //     visually-muted styling so it's obvious they don't count
  //     toward any running balance.
  //   • Respects the ChildScope selection: when the user is viewing
  //     a single child (from the app-wide child switcher) we filter
  //     direct payments down to expenses that include that child.
  //     When the switcher is on "All children" (selectedChildId ==
  //     null) we show every payment.
  //   • Preserved child-ids come from `expense_child_ids` on the
  //     timeline item (backend enrichment added in the same iter).
  const { selectedChildId } = useChildScope();
  const [showVoided, setShowVoided] = useState(false);

  // Child filter — payments without a linked expense (shouldn't
  // happen in practice) always pass through so we never accidentally
  // hide real money.
  const scopedPayments = useMemo(() => {
    if (!selectedChildId) return payments || [];
    return (payments || []).filter((p) => {
      const kids = p.expense_child_ids || p.child_ids;
      if (!kids || kids.length === 0) return true;
      return kids.includes(selectedChildId);
    });
  }, [payments, selectedChildId]);

  const livePayments = scopedPayments.filter((p) => !p.voided);
  const voidedPayments = scopedPayments.filter((p) => p.voided);

  // Total (live) in this scope — recompute so the child-filter view
  // shows the accurate scoped total, not the whole-household total.
  const scopedLiveTotal = livePayments.reduce(
    (acc, p) => acc + (typeof p.amount === "number" ? p.amount : parseFloat(p.amount || 0)),
    0,
  );

  if (!payments || payments.length === 0) return null;
  if (scopedPayments.length === 0) {
    // The parent has direct payments but none for the currently-
    // selected child — show a tiny "filtered out" hint rather than a
    // blank block.
    return (
      <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="direct-payments-received-scoped-empty">
        <CardContent className="p-5 text-sm text-[#6F828A]">
          <div className="flex items-center gap-2">
            <ArrowDownRight className="w-4 h-4 text-[#116B6E]" strokeWidth={1.5} />
            <span>No direct payments for the selected child.</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#D9E1E3] rounded-md shadow-none" data-testid="direct-payments-received">
      <CardContent className="p-5">
        <div className="flex items-baseline justify-between gap-2 flex-wrap mb-4">
          <div>
            <div className="flex items-center gap-2">
              <ArrowDownRight className="w-5 h-5 text-[#116B6E]" strokeWidth={1.5} />
              <span className="font-heading text-lg text-[#2E3A3F]">Direct payments received</span>
            </div>
            <p className="text-xs text-[#6F828A] mt-1 max-w-2xl">
              Individual payments your co-parent has settled against specific expenses.
              {selectedChildId ? " Filtered to the selected child." : " Listed alongside the lump-sum credits above so you have one combined audit trail."}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
              Total (live){selectedChildId ? ", this child" : ""}
            </div>
            <div className="font-mono-data text-lg text-[#116B6E] tabular-nums">
              {selectedChildId ? fmt(scopedLiveTotal, currency) : fmt(totals?.direct_received_live || 0, currency)}
            </div>
          </div>
        </div>

        {livePayments.length === 0 && voidedPayments.length > 0 && (
          <p className="text-xs text-[#6F828A] italic mb-3">
            No live direct payments{selectedChildId ? " for the selected child" : ""} — only voided remediation entries below.
          </p>
        )}

        {livePayments.length > 0 && (
          <ul className="space-y-2" data-testid="direct-payments-live-list">
            {livePayments.map((p) => (
              <DirectPaymentRow key={p.id} p={p} currency={currency} />
            ))}
          </ul>
        )}

        {voidedPayments.length > 0 && (
          <div className="mt-4 pt-3 border-t border-dashed border-[#D9E1E3]">
            <button
              type="button"
              onClick={() => setShowVoided((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs text-[#6F828A] hover:text-[#2E3A3F] transition-colors"
              data-testid="direct-payments-voided-toggle"
              aria-expanded={showVoided}
            >
              {showVoided
                ? <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.6} />
                : <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.6} />}
              <span>
                {showVoided ? "Hide" : "Show"} {voidedPayments.length} voided {voidedPayments.length === 1 ? "entry" : "entries"}
              </span>
              <span className="text-[#B8C4C7]">·</span>
              <span className="text-[#B8C4C7]">
                excluded from all totals
              </span>
            </button>
            {showVoided && (
              <ul className="space-y-2 mt-3 opacity-80" data-testid="direct-payments-voided-list">
                {voidedPayments.map((p) => (
                  <DirectPaymentRow key={p.id} p={p} currency={currency} />
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Single-row renderer, extracted so the live + voided lists share
 * exactly the same layout / status pills / metadata.
 */
function DirectPaymentRow({ p, currency }) {
  return (
    <li
      className={`border rounded-sm p-3 ${
        p.voided
          ? "border-[#E8B8B2] bg-[#FBEFED]"
          : p.disputed
            ? "border-[#E8DBA8] bg-[#FBF6E5]"
            : "border-[#D9E1E3] bg-white"
      }`}
      data-testid={`direct-payment-row-${p.id}`}
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium text-[#2E3A3F] truncate" data-testid={`direct-payment-title-${p.id}`}>
            {p.expense_title}
          </div>
          <div className="text-[11px] text-[#6F828A] mt-0.5 flex flex-wrap gap-x-2">
            <span>{p.date}</span>
            {p.method && <span>· {p.method}</span>}
            {p.reference && <span className="italic">· ref {p.reference}</span>}
            {p.recorded_by_name && <span>· logged by {p.recorded_by_name}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono-data tabular-nums text-[#2E3A3F]">{fmt(p.amount, p.currency || currency)}</div>
          <div className="mt-1 flex gap-1 flex-wrap justify-end">
            {p.voided && (
              <span className="text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm bg-[#8C2A22] text-white">Voided</span>
            )}
            {p.disputed && !p.voided && (
              <span className="text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm bg-[#A67C00] text-white">Disputed</span>
            )}
            {p.confirmed && !p.disputed && !p.voided && (
              <span className="text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm bg-[#116B6E] text-white">Confirmed</span>
            )}
            {!p.confirmed && !p.disputed && !p.voided && (
              <span className="text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm bg-[#6F828A] text-white">Pending</span>
            )}
          </div>
        </div>
      </div>
      {(p.note || p.voided_reason) && (
        <p className="text-xs text-[#2E3A3F] mt-2 whitespace-pre-wrap">
          {p.voided_reason ? `Voided: ${p.voided_reason}` : p.note}
        </p>
      )}
    </li>
  );
}

function SummaryTile({ label, value, highlight, testid }) {
  return (
    <div
      className={`rounded-sm p-3 border ${highlight ? "border-[#A7D8DA] bg-[#F2F8F8]" : "border-[#D9E1E3] bg-white"}`}
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-[0.08em] text-[#6F828A]">{label}</div>
      <div className={`mt-1 font-mono-data text-lg ${highlight ? "text-[#116B6E] font-semibold" : "text-[#2E3A3F]"}`}>{value}</div>
    </div>
  );
}

function RecordPaymentDialog({ open, onClose, onSaved, currency, childList, coparents }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    amount: "",
    payment_date: new Date().toISOString().slice(0, 10),
    paid_by: "",
    note: "",
    child_ids: [],
    shared_with_coparent: true,
    // v77.99.109 — Opt-in to immediately auto-allocate the credit to
    // the oldest open expenses (oldest-first) right after recording.
    // Default OFF — keeps the conservative "leave unallocated until
    // the parent reviews" behaviour from earlier iterations.
    auto_allocate: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error("Enter a positive amount.");
      return;
    }
    setBusy(true);
    try {
      const created = await api.post("/expenses/payments/credits", {
        amount: parseFloat(form.amount),
        currency,
        payment_date: form.payment_date,
        paid_by: form.paid_by || null,
        note: form.note,
        child_ids: form.child_ids,
        shared_with_coparent: form.shared_with_coparent,
      });
      if (form.auto_allocate && created?.data?.id) {
        try {
          const r = await api.post(
            `/expenses/payments/credits/${created.data.id}/allocate`,
            { mode: "auto_oldest" },
          );
          const n = (r?.data?.affected_expense_ids || []).length;
          if (n > 0) {
            toast.success(`Payment recorded · auto-allocated to ${n} expense${n === 1 ? "" : "s"}.`);
          } else {
            toast.success("Payment recorded · no open expenses to allocate to yet.");
          }
        } catch {
          // The credit itself was recorded fine; surface the partial
          // success and let the user allocate manually from the list.
          toast.success("Payment recorded. Auto-allocation failed — allocate manually.");
        }
      } else {
        toast.success("Payment received recorded.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record payment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record payment received</DialogTitle>
          <DialogDescription>
            This adds an unallocated payment credit. Individual expenses stay open until you allocate the credit to them.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="rp-amount">Amount received ({currency}) *</Label>
            <Input id="rp-amount" type="number" step="0.01" min="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="record-payment-amount" required />
          </div>
          <div>
            <Label htmlFor="rp-date">Date received *</Label>
            <Input id="rp-date" type="date" value={form.payment_date}
              onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              data-testid="record-payment-date" required />
          </div>
          {coparents.length > 0 && (
            <div>
              <Label htmlFor="rp-paidby">Paid by</Label>
              <Select value={form.paid_by} onValueChange={(v) => setForm({ ...form, paid_by: v })}>
                <SelectTrigger data-testid="record-payment-paidby">
                  <SelectValue placeholder="Select co-parent (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {coparents.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {childList && childList.length > 0 && (
            <div>
              <Label>Related to children (optional)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {childList.map((c) => (
                  <label key={c.id} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                    <Checkbox
                      checked={form.child_ids.includes(c.id)}
                      onCheckedChange={(v) => {
                        const set = new Set(form.child_ids);
                        if (v) set.add(c.id); else set.delete(c.id);
                        setForm({ ...form, child_ids: Array.from(set) });
                      }}
                      data-testid={`record-payment-child-${c.id}`}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="rp-note">Note (optional)</Label>
            <Textarea id="rp-note" rows={3} value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. Lump sum transfer received 10 Jun"
              data-testid="record-payment-note" />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="rp-shared" className="cursor-pointer">Share with co-parent</Label>
            <Switch id="rp-shared" checked={form.shared_with_coparent}
              onCheckedChange={(v) => setForm({ ...form, shared_with_coparent: v })}
              data-testid="record-payment-shared" />
          </div>
          {/* v77.99.109 — Skip a step for the common case where a
              parent receives a lump sum that should be applied to
              the oldest open expenses immediately. The credit's
              allocations are still per-allocation reversible via the
              Allocate dialog or the expense detail, so this is a
              safe convenience toggle rather than a destructive one. */}
          <div className="flex items-start justify-between gap-3 bg-[#F4F7F8] border border-[#D9E1E3] rounded-sm p-2.5">
            <div className="flex-1 min-w-0">
              <Label htmlFor="rp-auto-allocate" className="cursor-pointer text-sm">
                Auto-allocate to oldest open expenses
              </Label>
              <div className="text-[11px] text-[#6F828A] mt-0.5">
                Applies this payment to your oldest open expenses now. You can review and reverse individual allocations later.
              </div>
            </div>
            <Switch id="rp-auto-allocate" checked={form.auto_allocate}
              onCheckedChange={(v) => setForm({ ...form, auto_allocate: v })}
              data-testid="record-payment-auto-allocate" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy}
              className="bg-[#116B6E] hover:bg-[#0F5658] text-white"
              data-testid="record-payment-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Record payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AllocateDialog({ credit, onClose, onSaved, onChanged, prefilledPick }) {
  const [mode, setMode] = useState(prefilledPick ? "manual" : "auto_oldest");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState([]);
  const [picks, setPicks] = useState(
    prefilledPick ? { [prefilledPick.expense_id]: (prefilledPick.amount || 0).toFixed(2) } : {},
  );
  // Local copy of the credit so the dialog reflects deallocate clicks immediately.
  const [liveCredit, setLiveCredit] = useState(credit);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [er, cr] = await Promise.all([
        api.get(`/expenses/payments/credits/${credit.id}/eligible-expenses`),
        api.get(`/expenses/payments/credits`),
      ]);
      setEligible(er.data || []);
      const fresh = (cr.data || []).find((c) => c.id === credit.id);
      if (fresh) setLiveCredit(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load expenses.");
    } finally {
      setLoading(false);
    }
  }, [credit.id]);

  useEffect(() => { reload(); }, [reload]);

  const remaining = liveCredit.remaining_amount || 0;
  const ccy = liveCredit.currency || "EUR";

  // Build map: expense_id → title/date for the existing-allocations
  // section. We rely on `eligible` which includes already-allocated rows.
  const expensesById = useMemo(() => {
    const m = {};
    for (const e of eligible) m[e.id] = e;
    return m;
  }, [eligible]);

  const totalPicked = useMemo(() => {
    return Object.values(picks).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  }, [picks]);

  const overLimit = totalPicked > remaining + 0.005;
  // Disable submit when manual mode + (nothing picked OR over-limit OR any
  // single row exceeds its outstanding cap).
  const anyRowOver = useMemo(() => {
    for (const [eid, raw] of Object.entries(picks)) {
      const v = parseFloat(raw) || 0;
      const cap = expensesById[eid]?.outstanding ?? 0;
      if (v > cap + 0.005) return true;
    }
    return false;
  }, [picks, expensesById]);

  const manualInvalid = mode === "manual" && (totalPicked <= 0 || overLimit || anyRowOver);
  const submitDisabled = busy || loading || remaining <= 0.005 || manualInvalid;

  const fillRemainder = (e) => {
    const cap = e.outstanding;
    const stillAvailable = Math.max(0, remaining - (totalPicked - (parseFloat(picks[e.id]) || 0)));
    const v = Math.min(cap, stillAvailable);
    setPicks({ ...picks, [e.id]: v.toFixed(2) });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = { mode };
      if (mode === "manual") {
        body.allocations = Object.entries(picks)
          .filter(([, v]) => parseFloat(v) > 0)
          .map(([expense_id, v]) => ({ expense_id, amount: parseFloat(v) }));
        if (body.allocations.length === 0) {
          toast.error("Select at least one expense to allocate to.");
          setBusy(false);
          return;
        }
      }
      await api.post(`/expenses/payments/credits/${credit.id}/allocate`, body);
      toast.success("Payment allocated.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Allocation failed.");
    } finally {
      setBusy(false);
    }
  };

  const deallocateOne = async (allocId) => {
    if (!window.confirm("Remove this allocation? The expense's outstanding amount will go back up by this amount.")) return;
    try {
      await api.delete(`/expenses/payments/credits/${credit.id}/allocations/${allocId}`);
      toast.success("Allocation removed.");
      setPicks({});  // reset picks since outstanding amounts changed
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove allocation.");
    }
  };

  const allocatableRows = useMemo(
    () => eligible.filter((e) => e.outstanding > 0.005),
    [eligible],
  );

  const existingAllocs = liveCredit.allocations || [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="allocate-dialog">
        <DialogHeader>
          <DialogTitle>Allocate {fmt(remaining, ccy)}</DialogTitle>
          <DialogDescription>
            Choose how to allocate this credit. You can leave it unallocated and the credit will stay as a running balance.
          </DialogDescription>
        </DialogHeader>

        {/* Existing allocations on this credit — with deallocate buttons */}
        {existingAllocs.length > 0 && (
          <div className="space-y-2" data-testid="allocate-existing">
            <div className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">
              Currently allocated to {existingAllocs.length} expense{existingAllocs.length === 1 ? "" : "s"}
            </div>
            <ul className="space-y-1.5">
              {existingAllocs.map((a) => {
                const e = expensesById[a.expense_id];
                return (
                  <li
                    key={a.id}
                    className="flex items-center gap-2 text-xs p-2 border border-[#D9E1E3] rounded-sm bg-[#F2F8F8]"
                    data-testid={`existing-alloc-${a.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[#2E3A3F] truncate" title={e?.title || a.expense_id}>
                        {e?.title || "(expense)"}
                      </div>
                      <div className="text-[#6F828A]">
                        {e?.date || ""} · Applied {fmt(a.amount, ccy)}
                        {a.auto ? " · auto" : ""}
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => deallocateOne(a.id)}
                      className="text-[#A93A2F] hover:bg-[#FBEFEE] h-7 px-2"
                      data-testid={`deallocate-${a.id}`}
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={1.8} />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {remaining <= 0.005 ? (
          <div className="p-4 text-sm text-[#6F828A] border border-[#D9E1E3] rounded-sm bg-[#FAFBFB]">
            This credit is fully allocated. Remove an allocation above to free up some balance.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <label
                className="flex items-start gap-2 cursor-pointer p-3 border border-[#D9E1E3] rounded-sm hover:bg-[#F2F8F8]"
                data-testid="allocate-mode-auto"
              >
                <input
                  type="radio"
                  name="alloc-mode"
                  checked={mode === "auto_oldest"}
                  onChange={() => setMode("auto_oldest")}
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-medium">Auto-allocate oldest expenses first</div>
                  <div className="text-xs text-[#6F828A]">
                    Applies the credit to your oldest open expenses until it&apos;s exhausted or no more open expenses remain.
                  </div>
                </div>
              </label>
              <label
                className="flex items-start gap-2 cursor-pointer p-3 border border-[#D9E1E3] rounded-sm hover:bg-[#F2F8F8]"
                data-testid="allocate-mode-manual"
              >
                <input
                  type="radio"
                  name="alloc-mode"
                  checked={mode === "manual"}
                  onChange={() => setMode("manual")}
                  className="mt-1"
                />
                <div>
                  <div className="text-sm font-medium">Manually pick expenses</div>
                  <div className="text-xs text-[#6F828A]">Choose specific expenses and amounts below.</div>
                </div>
              </label>
            </div>

            {mode === "manual" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#6F828A]">
                    Total picked:{" "}
                    <strong
                      className={overLimit ? "text-[#A93A2F]" : "text-[#116B6E]"}
                      data-testid="allocate-total-picked"
                    >
                      {fmt(totalPicked, ccy)}
                    </strong>{" "}
                    / {fmt(remaining, ccy)}
                  </span>
                  {(overLimit || anyRowOver) && (
                    <span className="inline-flex items-center gap-1 text-[#A93A2F] font-medium" data-testid="allocate-over-limit">
                      <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.8} />
                      {overLimit ? "Exceeds remaining" : "A row exceeds its outstanding"}
                    </span>
                  )}
                </div>
                {loading ? (
                  <div className="text-sm text-[#6F828A] py-6 text-center">
                    <Loader2 className="w-4 h-4 inline animate-spin mr-2" strokeWidth={1.8} /> Loading expenses…
                  </div>
                ) : allocatableRows.length === 0 ? (
                  <div className="p-4 text-sm text-[#6F828A] border border-dashed border-[#D9E1E3] rounded-sm flex items-start gap-2" data-testid="allocate-no-expenses">
                    <FileWarning className="w-4 h-4 mt-0.5 text-[#6F828A] flex-shrink-0" strokeWidth={1.6} />
                    <span>No expenses with outstanding amounts available to allocate against.</span>
                  </div>
                ) : (
                  <ul className="space-y-1.5 max-h-[300px] overflow-y-auto">
                    {allocatableRows.map((e) => {
                      const cap = e.outstanding;
                      const cur = parseFloat(picks[e.id]) || 0;
                      const rowOver = cur > cap + 0.005;
                      return (
                        <li
                          key={e.id}
                          className="flex items-center gap-2 text-xs p-2 border border-[#D9E1E3] rounded-sm"
                          data-testid={`allocate-expense-${e.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-[#2E3A3F] truncate" title={e.title}>{e.title}</div>
                            <div className="text-[#6F828A]">
                              {e.date} · Total {fmt(e.amount, e.currency)} · Outstanding{" "}
                              <strong className="text-[#116B6E]">{fmt(cap, e.currency)}</strong>
                              {e.already_allocated_here > 0.005 && (
                                <span> · already from this credit {fmt(e.already_allocated_here, e.currency)}</span>
                              )}
                            </div>
                          </div>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max={cap}
                            placeholder="0.00"
                            value={picks[e.id] || ""}
                            onChange={(ev) => setPicks({ ...picks, [e.id]: ev.target.value })}
                            className={`w-[100px] h-8 rounded-sm ${rowOver ? "border-[#A93A2F] focus-visible:ring-[#A93A2F]" : "border-[#D9E1E3]"}`}
                            data-testid={`allocate-pick-${e.id}`}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => fillRemainder(e)}
                            className="h-8 px-2 text-[11px] text-[#116B6E] hover:bg-[#D6F0EF]"
                            data-testid={`allocate-fill-${e.id}`}
                            title="Fill outstanding (capped at credit remaining)"
                          >
                            Fill
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            {existingAllocs.length > 0 || remaining <= 0.005 ? "Done" : "Leave unallocated"}
          </Button>
          {remaining > 0.005 && (
            <Button
              onClick={submit}
              disabled={submitDisabled}
              className="bg-[#116B6E] hover:bg-[#0F5658] text-white"
              data-testid="allocate-submit"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Allocating…</> : "Allocate"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// v77.99.115d — Two small sub-components for the credit row:
//
//   • CreditShareToggle — flips `shared_with_coparent` in-place via
//     PATCH /api/expenses/payments/credits/{id}. Shown as a compact
//     icon button (open padlock = shared, closed = private). Hover
//     tooltip explains the effect.
//
//   • CreditNotesTimeline — renders the immutable `additional_notes`
//     array (oldest first) below the original note, plus an inline
//     "+ Add note" affordance that POSTs to /credits/{id}/notes.
//     Notes can never be edited — only appended — preserving the
//     audit trail.

const CreditShareToggle = ({ credit, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const shared = !!credit?.shared_with_coparent;
  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/expenses/payments/credits/${credit.id}`, {
        shared_with_coparent: !shared,
      });
      toast.success(shared
        ? "Marked private — only you can see this credit and its note."
        : "Shared with co-parent."
      );
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update privacy");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      onClick={toggle}
      disabled={busy}
      title={shared ? "Currently shared with your co-parent. Click to make private." : "Currently private. Click to share with your co-parent."}
      className={shared ? "text-[#116B6E] hover:bg-[#D6F0EF]" : "text-[#5C4516] hover:bg-[#FFF8E8]"}
      data-testid={`credit-share-toggle-${credit.id}`}
      aria-pressed={shared}
    >
      {shared
        ? <Unlock className="w-3.5 h-3.5" strokeWidth={1.6} />
        : <Lock className="w-3.5 h-3.5" strokeWidth={1.6} />}
    </Button>
  );
};

const CreditNotesTimeline = ({ credit, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const notes = credit?.additional_notes || [];
  const submit = async () => {
    const text = draft.trim();
    if (!text) {
      toast.error("Note cannot be empty");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/expenses/payments/credits/${credit.id}/notes`, { text });
      toast.success("Note added");
      setDraft("");
      setAdding(false);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add note");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 space-y-1.5" data-testid={`credit-notes-timeline-${credit.id}`}>
      {notes.length > 0 && (
        <ul className="space-y-1.5">
          {notes.map((n) => (
            <li
              key={n.id}
              className="text-xs bg-[#F8FAFB] border border-[#EEF3F4] rounded-sm px-2 py-1.5"
              data-testid={`credit-note-entry-${n.id}`}
            >
              <p className="text-[#2E3A3F] whitespace-pre-wrap">{n.text}</p>
              <p className="text-[10px] text-[#6F828A] mt-0.5">
                Added by {n.by_name || "—"} · {(n.at || "").slice(0, 16).replace("T", " ")}
              </p>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <div className="space-y-1.5">
          <Textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a clarifying note. The original note stays as-is."
            className="text-xs"
            data-testid={`credit-note-input-${credit.id}`}
            maxLength={2000}
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={submit}
              disabled={busy || !draft.trim()}
              className="bg-[#1F9FA4] hover:bg-[#198185] text-white h-7 px-3 text-xs"
              data-testid={`credit-note-submit-${credit.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save note"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setAdding(false); setDraft(""); }}
              className="h-7 px-2 text-xs text-[#6F828A]"
              data-testid={`credit-note-cancel-${credit.id}`}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="text-[11px] text-[#116B6E] hover:underline inline-flex items-center gap-1"
          data-testid={`credit-note-add-${credit.id}`}
        >
          <MessageSquarePlus className="w-3 h-3" strokeWidth={1.6} /> Add note
        </button>
      )}
    </div>
  );
};
